import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row, Col, Card, Skeleton, Typography, Table } from 'antd';
import DashboardLineChart from '../../../components/DashboardLineChart';
import { getFeedbackSummary, getFeedbackTimeseries } from '../../../services/api/feedbackAnalytics';
import { timeRangeToRagPeriod } from '../../../utils/timeRange';
import { useDashboard } from '../DashboardContext';
import DashboardStatCard from '../DashboardStatCard';
import { formatNumber } from '../dashboardUtils';

const { Text } = Typography;

const FEEDBACK_SERIES = [
  { key: 'likes', label: 'Likes', color: '#10b981' },
  { key: 'dislikes', label: 'Dislikes', color: '#ef4444' },
];

export default function FeedbackSection() {
  const { keycloak, timeRange, showAll, isAdmin } = useDashboard();
  const [searchParams] = useSearchParams();
  const assistantId = searchParams.get('assistantId');

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);

  const platformFlag = showAll && isAdmin;
  const period = timeRangeToRagPeriod(timeRange);

  const apiOpts = useMemo(() => ({
    period,
    timeRange,
    showAll: platformFlag,
    assistantId: assistantId ? Number(assistantId) : null,
  }), [period, timeRange, platformFlag, assistantId]);

  const loadData = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    setLoading(true);
    try {
      const [sum, ts] = await Promise.all([
        getFeedbackSummary(keycloak, apiOpts),
        getFeedbackTimeseries(keycloak, apiOpts),
      ]);
      setSummary(sum);
      setTimeseries(ts);
    } finally {
      setLoading(false);
    }
  }, [keycloak, apiOpts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = [
    { title: 'Assistant', dataIndex: 'assistant_name', key: 'assistant_name', ellipsis: true },
    { title: 'Likes', dataIndex: 'likes', key: 'likes', width: 72, align: 'right' },
    { title: 'Dislikes', dataIndex: 'dislikes', key: 'dislikes', width: 88, align: 'right' },
    { title: 'Total', dataIndex: 'total', key: 'total', width: 72, align: 'right' },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <DashboardStatCard title="Total feedback" loading={loading}>
            <div className="stat-value">{formatNumber(summary?.total ?? 0)}</div>
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={8}>
          <DashboardStatCard title="Like rate" loading={loading}>
            <div className="stat-value">
              {summary?.total ? `${(summary.like_rate * 100).toFixed(1)}%` : '—'}
            </div>
            <Text type="secondary" className="dashboard-spend__meta">
              {summary?.likes ?? 0} likes
            </Text>
          </DashboardStatCard>
        </Col>
        <Col xs={24} sm={8}>
          <DashboardStatCard title="Net sentiment" loading={loading}>
            <div className="stat-value">{summary?.net_sentiment ?? 0}</div>
            <Text type="secondary" className="dashboard-spend__meta">
              {summary?.dislikes ?? 0} dislikes
            </Text>
          </DashboardStatCard>
        </Col>
      </Row>

      <Card className="modern-card" title={<span className="card-title">Feedback over time</span>} style={{ marginBottom: 20 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <DashboardLineChart
            data={timeseries}
            series={FEEDBACK_SERIES}
            yAxisFormatter={(v) => Math.round(v)}
            tooltipFormatter={(v) => Math.round(v)}
            emptyState={<Text type="secondary">No feedback in this period.</Text>}
          />
        )}
      </Card>

      {!loading && (summary?.by_assistant?.length > 0) && (
        <Table
          size="small"
          rowKey="assistant_id"
          columns={columns}
          dataSource={summary.by_assistant}
          pagination={false}
        />
      )}
    </>
  );
}
